// src/pages/ip/IPAddressesPage.tsx

import {
  AlertTriangle,
  CheckCircle2,
  EthernetPort,
  Filter,
  Globe,
  Loader2,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  Wifi,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "../components/ui/Toast";
import { fetchApp } from "../services/apiService";

interface RouterInterfacesResponse {
  router_id: string;
  router_name: string;
  router_ip: string;
  interfaces: any[];
}

interface IPAddress {
  id?: string;
  address: string;
  network?: string | null;
  interface: string;
  actual_interface?: string | null;
  invalid?: boolean;
  dynamic?: boolean;
  disabled: boolean;
  comment?: string | null;
}

interface RouterIPAddresses {
  router_id: string;
  router_name: string;
  router_ip: string;
  addresses: IPAddress[];
}

interface FlattenedIP extends IPAddress {
  routerName: string;
  routerId: string;
  routerIp: string;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading IP addresses…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while router IP assignments are being prepared.
          </p>

          <div className="relative mt-6 flex justify-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-teal-500 delay-150" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-500 delay-300" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ addr: IPAddress }> = ({ addr }) => {
  if (addr.invalid) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-100 px-2.5 py-1 text-[11px] font-black text-rose-700">
        <AlertTriangle className="h-3 w-3" />
        Invalid
      </span>
    );
  }

  if (addr.disabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
        <XCircle className="h-3 w-3" />
        Disabled
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
      <CheckCircle2 className="h-3 w-3" />
      Active
    </span>
  );
};

const TypeBadge: React.FC<{ dynamic?: boolean }> = ({ dynamic }) => {
  if (dynamic) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-[11px] font-black text-amber-800">
        <Zap className="h-3 w-3" />
        Dynamic
      </span>
    );
  }

  return (
    <span className="inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
      Static
    </span>
  );
};

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 6 }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 0
              ? "w-36"
              : index === 5
                ? "w-44"
                : index === 3 || index === 4
                  ? "w-20 rounded-full"
                  : "w-28"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-40 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>

      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const IPAddressesPage: React.FC = () => {
  const { showToast } = useToast();

  const [routerAddresses, setRouterAddresses] = useState<RouterIPAddresses[]>(
    [],
  );
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedRouterId, setSelectedRouterId] = useState<string>("all");

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      resetMessages();

      const routers = await fetchApp<RouterInterfacesResponse[]>("/interface/");
      const routerList = Array.isArray(routers) ? routers : [];
      const results: RouterIPAddresses[] = [];

      for (const r of routerList) {
        try {
          const data = await fetchApp<any[]>(
            `/interface/addresses?router_id=${r.router_id}`,
          );
          const list = Array.isArray(data) ? data : [];

          const addresses: IPAddress[] = list.map((item: any) => ({
            id: item[".id"],
            address: item.address,
            network: item.network ?? null,
            interface: item.interface,
            actual_interface: item["actual-interface"] ?? null,
            invalid: !!item.invalid,
            dynamic: !!item.dynamic,
            disabled: !!item.disabled,
            comment: item.comment ?? null,
          }));

          results.push({
            router_id: r.router_id,
            router_name: r.router_name,
            router_ip: r.router_ip,
            addresses,
          });
        } catch (innerErr: any) {
          console.error(`Failed to load IPs for ${r.router_name}`, innerErr);
          showToast(`Failed to load IPs for ${r.router_name}`, "error");
        }
      }

      setRouterAddresses(results);
      setSuccess("IP addresses loaded successfully.");
    } catch (err: any) {
      setError(err?.message || "Failed to load IP addresses.");
      showToast("Failed to load IP addresses", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allAddresses = useMemo<FlattenedIP[]>(() => {
    return routerAddresses.flatMap((router) =>
      router.addresses.map((addr) => ({
        ...addr,
        routerName: router.router_name,
        routerId: router.router_id,
        routerIp: router.router_ip,
      })),
    );
  }, [routerAddresses]);

  const filteredAddresses = useMemo(() => {
    return allAddresses.filter((item) => {
      if (
        selectedRouterId !== "all" &&
        item.routerId.toString() !== selectedRouterId
      ) {
        return false;
      }

      const term = search.toLowerCase().trim();
      if (!term) return true;

      return (
        item.address.toLowerCase().includes(term) ||
        Boolean(item.network?.toLowerCase().includes(term)) ||
        item.interface.toLowerCase().includes(term) ||
        item.routerName.toLowerCase().includes(term) ||
        Boolean(item.comment?.toLowerCase().includes(term))
      );
    });
  }, [allAddresses, search, selectedRouterId]);

  const stats = useMemo(() => {
    const total = filteredAddresses.length;
    let enabled = 0;
    let disabled = 0;
    let dynamic = 0;
    let invalid = 0;

    filteredAddresses.forEach((a) => {
      if (a.disabled) disabled += 1;
      else enabled += 1;

      if (a.dynamic) dynamic += 1;
      if (a.invalid) invalid += 1;
    });

    return { total, enabled, disabled, dynamic, invalid };
  }, [filteredAddresses]);

  if (loading && routerAddresses.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Layer 3 Network Management
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  IP Addresses
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  View IP assignments, networks, interfaces, and status across
                  your MikroTik routers.
                </p>
              </div>

              <button
                type="button"
                onClick={fetchAddresses}
                disabled={loading}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {loading ? "Refreshing…" : "Refresh Data"}
              </button>
            </div>
          </div>
        </div>

        {loading && routerAddresses.length > 0 && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing IP addresses…
          </div>
        )}

        {(error || success) && (
          <div
            className={`flex items-start justify-between gap-3 rounded-3xl border px-4 py-3.5 text-[13px] font-bold shadow-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700 shadow-rose-200/40"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 shadow-emerald-200/40"
            }`}
          >
            <div className="flex items-start gap-3">
              {error ? (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <p>{error || success}</p>
            </div>

            <button
              type="button"
              onClick={resetMessages}
              className={`rounded-xl p-1.5 transition-colors ${
                error
                  ? "text-rose-600 hover:bg-rose-100"
                  : "text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Total"
            value={stats.total}
            sub="Filtered IP addresses"
            icon={<Globe className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Active"
            value={stats.enabled}
            sub="Enabled assignments"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Dynamic"
            value={stats.dynamic}
            sub="Dynamic addresses"
            icon={<Zap className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Disabled"
            value={stats.disabled}
            sub="Disabled entries"
            icon={<XCircle className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Invalid"
            value={stats.invalid}
            sub="Needs checking"
            icon={<AlertTriangle className="h-5 w-5" />}
            tone={stats.invalid > 0 ? "rose" : "slate"}
          />
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <Wifi className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  IP Address List
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  Showing {filteredAddresses.length} of {allAddresses.length}{" "}
                  addresses
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
              <div className="relative w-full lg:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search IP, network, interface..."
                  className={`${inputCls} pl-10`}
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="relative w-full lg:w-72">
                <Filter className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  value={selectedRouterId}
                  onChange={(e) => setSelectedRouterId(e.target.value)}
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                >
                  <option value="all">All Routers</option>
                  {routerAddresses.map((r) => (
                    <option key={r.router_id} value={r.router_id}>
                      {r.router_name}
                    </option>
                  ))}
                </select>

                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {(search || selectedRouterId !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setSelectedRouterId("all");
                  }}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Address / Network</th>
                  <th className="px-5 py-3.5 font-black">Interface</th>
                  <th className="px-5 py-3.5 font-black">Router</th>
                  <th className="px-5 py-3.5 font-black">Type</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                  <th className="px-5 py-3.5 font-black">Comment</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableSkeleton key={index} />
                  ))
                ) : filteredAddresses.length > 0 ? (
                  filteredAddresses.map((addr, idx) => (
                    <tr
                      key={`${addr.routerId}-${addr.id || idx}`}
                      className="transition-colors hover:bg-emerald-50/60"
                    >
                      <td className="px-5 py-4">
                        <p className="font-mono text-[12.5px] font-black text-slate-900">
                          {addr.address}
                        </p>

                        {addr.network && (
                          <p className="mt-0.5 font-mono text-[11px] font-medium text-slate-500">
                            Network: {addr.network}
                          </p>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5 text-[12.5px] font-bold text-slate-700">
                          <EthernetPort className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          {addr.interface}
                        </div>

                        {addr.actual_interface &&
                          addr.actual_interface !== addr.interface && (
                            <p className="mt-0.5 pl-5 text-[11px] font-medium text-slate-400">
                              Actual: {addr.actual_interface}
                            </p>
                          )}
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                          <Server className="h-3 w-3" />
                          {addr.routerName}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <TypeBadge dynamic={addr.dynamic} />
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge addr={addr} />
                      </td>

                      <td
                        className="max-w-[260px] truncate px-5 py-4 text-[12px] font-medium text-slate-500"
                        title={addr.comment || ""}
                      >
                        {addr.comment || "—"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Globe className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No IP addresses found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        No IP address records match your current filters.
                      </p>

                      {(search || selectedRouterId !== "all") && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearch("");
                            setSelectedRouterId("all");
                          }}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear all filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filteredAddresses.length > 0 ? (
              filteredAddresses.map((addr, idx) => (
                <div
                  key={`${addr.routerId}-${addr.id || idx}`}
                  className="px-4 py-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-[13px] font-black text-slate-900">
                        {addr.address}
                      </p>

                      {addr.network && (
                        <p className="mt-0.5 truncate font-mono text-[11.5px] font-medium text-slate-500">
                          Network: {addr.network}
                        </p>
                      )}
                    </div>

                    <StatusBadge addr={addr} />
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <TypeBadge dynamic={addr.dynamic} />

                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                      <Server className="h-3 w-3" />
                      {addr.routerName}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Interface
                      </p>

                      <p className="mt-1 flex items-center gap-1.5 text-[12.5px] font-black text-slate-800">
                        <EthernetPort className="h-3.5 w-3.5 text-slate-400" />
                        {addr.interface}
                      </p>

                      {addr.actual_interface &&
                        addr.actual_interface !== addr.interface && (
                          <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                            Actual: {addr.actual_interface}
                          </p>
                        )}
                    </div>

                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                        State
                      </p>

                      <p className="mt-1 flex items-center gap-1.5 text-[12.5px] font-black text-slate-800">
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                        {addr.invalid
                          ? "Invalid"
                          : addr.disabled
                            ? "Disabled"
                            : "Active"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Comment
                    </p>

                    <p className="mt-1 text-[12px] font-medium text-slate-700">
                      {addr.comment || "—"}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Globe className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No IP addresses found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  No IP address records match your current filters.
                </p>

                {(search || selectedRouterId !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      setSelectedRouterId("all");
                    }}
                    className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredAddresses.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {allAddresses.length}
              </span>{" "}
              addresses
            </p>

            {(search || selectedRouterId !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setSelectedRouterId("all");
                }}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IPAddressesPage;
